<template>
  <div class="companies-subscriptions-page">
    <DefaultHeader title="Мои подписки на компании" />

      <div class="content">

        <div v-if="isLoading" class="subscriptions-wrapper">
          <SubscriptionSkeleton v-for="n in 4" :key="n" />
        </div>
        
        <div v-else-if="errorMessage" class="placeholder">{{ errorMessage }}</div>
        
        <div v-else-if="subscribedCompanies.length === 0" class="empty-state">
          <img src="../assets/empty-subscriptions.png" alt="Нет подписок" class="empty-image" />
          <div class="empty-title">У вас нет подписок</div>
          <div class="empty-description">
            У вас пока нет подписок на компании.<br />
            Подпишитесь, чтобы не пропустить интересные вакансии.
          </div>
        </div>
        
        <div v-else class="subscriptions-wrapper">
          <SubscriptionCompany
            v-for="company in subscribedCompanies"
            :key="company.id"
            :company="company"
            @unsubscribe="handleUnsubscribe"
          />
        </div>
      </div>

    <MiniFooter />
  </div>
  <SuccessSnackbar
    :message="snackbarMessage"
    :type="snackbarType"
    :show="snackbarVisible"
  />
</template>

<script setup>

import { ref, onMounted, nextTick } from 'vue';
import { getAuth, onAuthStateChanged } from "firebase/auth";

import SubscriptionSkeleton from "../components/SubscriptionSkeleton.vue";
import DefaultHeader from "../components/DefaultHeader.vue";
import MiniFooter from "../components/MiniFooter.vue";
import SubscriptionCompany from "../components/SubscriptionCompany.vue";
import SubscriptionsService from "../api/SubscriptionsService.js";
import SuccessSnackbar from "../components/SuccessSnackbar.vue";

const subscribedCompanies = ref([]);
const isLoading = ref(true);
const errorMessage = ref("");

const snackbarMessage = ref("");
const snackbarVisible = ref(false);
const snackbarType = ref("success");

const showSnackbar = (msg, type = "success") => {
  snackbarMessage.value = msg;
  snackbarType.value = type;
  snackbarVisible.value = false;
  nextTick(() => {
    snackbarVisible.value = true;
  });
};

const handleUnsubscribe = async (companyId) => {
  try {
    const success = await SubscriptionsService.unsubscribeFromCompany(companyId);
    if (success) {
      subscribedCompanies.value = subscribedCompanies.value.filter(
        (c) => c.id !== companyId
      );
      showSnackbar("Вы отписались от компании");
    }
  } catch (error) {
    showSnackbar("Не удалось отписаться", "error");
  }
};

onMounted(async () => {
  try {
    const companies =
      await SubscriptionsService
        .getSubscribedCompanies();

    subscribedCompanies.value =
      companies;

  } catch (error) {
    console.error(
      "❌ Ошибка загрузки подписок:",
      error
    );

    errorMessage.value =
      "Не удалось загрузить подписки";

  } finally {
    isLoading.value =
      false;
  }
});

</script>

<style scoped>

.companies-subscriptions-page {
  width: 100%;
  min-height: 100vh;

  display: flex;
  flex-direction: column;

  background-color: var(--bg);
  overflow-x: hidden;
}

.content {
  flex: 1;

  width: 100%;
  max-width: 900px;

  padding: 40px 20px;
  margin: 0 auto;

  box-sizing: border-box;
}

.placeholder {
  color: var(--text-secondary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 16px;
}

.empty-state {
  text-align: center;
  margin-top: 80px;
  color: var(--text-primary);
}

.empty-image {
  width: 160px;
  margin-bottom: 24px;
}

.empty-title {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  margin-bottom: 10px;
}

.empty-description {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.subscriptions-wrapper {
  width: 100%;

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));

  gap: 20px;
  margin-top: 40px;
}

@media (max-width: 430px) {
  .companies-subscriptions-page {
    min-height: 100dvh;
    height: 100dvh;

    overflow: hidden;
  }

  .content {
    flex: 1;

    padding: 72px 14px 110px;

    overflow-y: auto;
    overflow-x: hidden;

    -webkit-overflow-scrolling: touch;
  }

  .subscriptions-wrapper {
    grid-template-columns: 1fr;

    gap: 14px;
    margin-top: 0;
  }

  .placeholder {
    font-size: 14px;
    text-align: center;

    margin-top: 80px;
  }

  .empty-state {
    min-height: calc(100dvh - 230px);

    margin-top: 0;
    padding: 24px 12px 90px;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .empty-image {
    width: 92px;
    margin-bottom: 18px;
  }

  .empty-title {
    font-size: 20px;
    line-height: 1.15;
    margin-bottom: 8px;
  }

  .empty-description {
    max-width: 280px;

    font-size: 14px;
    line-height: 1.45;
  }
}

</style>